import { fetchBaseQuery, type FetchArgs } from '@reduxjs/toolkit/query';

// Not through the module's barrel: that also exports the thunk, which reaches
// back into the api slices this file underpins.
import {
  commitHeldRoots,
  commitOperatorContext,
  lowestHeldRoot,
  selectOperatorInstitutionId,
} from './operatorContext/operatorContext';
import {
  decodeOperatorContextRefusal,
  OPERATOR_CONTEXT_HEADER,
} from './operatorContext/operatorContext.contract';
import {
  selectIsSharedAccountSessionActive,
  selectSharedAccountSessionToken,
} from './sharedSession/sharedAccountSession';
import { selectIsSharedSessionActive, selectShareToken } from './sharedSession/sharedSession';
import type { RootState } from './store';
import { getBackrsToken } from '../utils/tokenHelpers';

/**
 * Get the appropriate access token based on session state
 * Priority: shared account session > shared session > user token
 */
const getAccessToken = (state: RootState): string | null => {
  const isSharedAccountSessionActive = selectIsSharedAccountSessionActive(state);
  const sharedAccountSessionToken = selectSharedAccountSessionToken(state);

  if (isSharedAccountSessionActive && sharedAccountSessionToken) {
    return sharedAccountSessionToken;
  }

  const isSharedSessionActive = selectIsSharedSessionActive(state);
  const sharedSessionToken = selectShareToken(state);

  if (isSharedSessionActive && sharedSessionToken) {
    return sharedSessionToken;
  }

  return getBackrsToken();
};

/**
 * Inverts getAccessToken's priority: prefer the user's token over any active
 * share token. Lubav's memory and profile are scoped to the authenticated
 * identity, so a logged-in user must hit Lubav as themselves rather than as
 * the share-resource owner. Falls back to getAccessToken when no user token
 * is present.
 */
export const getLubavAccessToken = (state: RootState): string | null => {
  const backrsToken = getBackrsToken();

  if (backrsToken) {
    return backrsToken;
  }

  return getAccessToken(state);
};

/**
 * Fetch base query with auth
 */
const authedFetch = fetchBaseQuery({
  baseUrl: `${process.env.REACT_APP_API_BASEURL}`,
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootState;
    const accessToken = getAccessToken(state);

    if (accessToken) {
      headers.set('authorization', `Bearer ${accessToken}`);
    }

    // No header sent means the server resolves the context implicitly — the wire contract.
    const operatorInstitutionId = selectOperatorInstitutionId(state);

    if (operatorInstitutionId !== null) {
      headers.set(OPERATOR_CONTEXT_HEADER, String(operatorInstitutionId));
    }

    headers.set('Content-Type', 'application/json');
    headers.set('Accept', 'application/json');

    return headers;
  },
});

const isMutating = (args: string | FetchArgs): boolean =>
  typeof args !== 'string' && (args.method ?? 'GET').toUpperCase() !== 'GET';

/**
 * Only the bare request retries: a refusal to a request that already carried a context is the
 * answer, not a prompt. A write is never among them — re-issuing one would commit it under a
 * root the operator never chose, and the refusal is what raises the picker for them to choose.
 */
export const baseQueryWithAuth: typeof authedFetch = async (args, api, extraOptions) => {
  const wasStamped = selectOperatorInstitutionId(api.getState() as RootState) !== null;
  const result = await authedFetch(args, api, extraOptions);
  const heldRoots = decodeOperatorContextRefusal(result.error);

  if (heldRoots === null) return result;

  api.dispatch(commitHeldRoots(heldRoots));

  if (wasStamped || isMutating(args)) return result;

  const state = api.getState() as RootState;
  const institutionId = selectOperatorInstitutionId(state) ?? lowestHeldRoot(heldRoots)?.id ?? null;

  if (institutionId === null) return result;

  api.dispatch(
    commitOperatorContext({
      institutionId,
      ownerUserId: state.user.user?.id ?? null,
      heldRoots,
    }),
  );

  return authedFetch(args, api, extraOptions);
};

/**
 * Create base query with custom token
 * @param token - Token to use for auth
 */
export const baseQueryWithCustomToken = (accessToken: string) =>
  fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_API_BASEURL}`,
    prepareHeaders: (headers) => {
      headers.set('authorization', `Bearer ${accessToken}`);
      headers.set('Content-Type', 'application/json');
      headers.set('Accept', 'application/json');

      return headers;
    },
  });

/**
 * Fetch base query for Lubav endpoints
 */
export const lubavAppBaseQuery = fetchBaseQuery({
  baseUrl: `${process.env.REACT_APP_LUBAV_API_BASEURL}`,
  prepareHeaders: (headers, { getState }) => {
    const accessToken = getLubavAccessToken(getState() as RootState);

    if (accessToken) {
      headers.set('authorization', `Bearer ${accessToken}`);
    }

    headers.set('Content-Type', 'application/json');
    headers.set('Accept', 'application/json');

    return headers;
  },
});

/**
 * Create Lubav base query with custom token
 * @param token - Token to use for auth
 */
export const lubavAppBaseQueryWithCustomToken = (accessToken: string) =>
  fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_LUBAV_API_BASEURL}`,
    prepareHeaders: (headers) => {
      headers.set('authorization', `Bearer ${accessToken}`);
      headers.set('Content-Type', 'application/json');
      headers.set('Accept', 'application/json');

      return headers;
    },
  });

/**
 * Fetch base query without auth (for public/pre-login endpoints)
 */
export const baseQueryWithoutAuth = fetchBaseQuery({
  baseUrl: `${process.env.REACT_APP_API_BASEURL}`,
  prepareHeaders: (headers) => {
    headers.set('Content-Type', 'application/json');
    headers.set('Accept', 'application/json');

    return headers;
  },
});
